import { useEffect, useRef, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { queryClient } from '@futrli/views/core/queryClient';
import { exportedReportsQueryKey } from '@futrli/views/queries/reportSchedules';

import { getExportedReportDetails } from '../../../../core/api/reporting/report-document-export';

const REFRESH_INTERVAL = 2000;

interface UseExportPollingParams {
  exportedReportId: string | undefined;
  workspaceId: string;
  organisationId: string;
  isConsolidation: boolean;
  enabled: boolean;
}

interface UseExportPollingResult {
  isCompleted: boolean;
  errorMessage: string | undefined;
  reset: () => void;
}

export const useExportPolling = ({
  exportedReportId,
  workspaceId,
  organisationId,
  isConsolidation,
  enabled
}: UseExportPollingParams): UseExportPollingResult => {
  const intl = useIntl();
  const pollingRef = useRef<ReturnType<typeof setInterval>>();
  const [isCompleted, setIsCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const reset = useCallback(() => {
    setIsCompleted(false);
    setErrorMessage(undefined);
  }, []);

  useEffect(() => {
    if (!enabled || !exportedReportId) {
      return;
    }

    pollingRef.current = setInterval(() => {
      void (async () => {
        try {
          const data = await getExportedReportDetails(workspaceId, organisationId, exportedReportId, isConsolidation);
          if (data.status === 'rendered') {
            clearInterval(pollingRef.current);
            queryClient.invalidateQueries({
              queryKey: exportedReportsQueryKey(organisationId, workspaceId, isConsolidation)
            });
            setIsCompleted(true);
          } else if (data.status === 'failed-rendering') {
            clearInterval(pollingRef.current);
            setErrorMessage(intl.formatMessage({ id: 'reporting.export_failed', defaultMessage: 'Export failed. Try again.' }));
          }
        } catch {
          clearInterval(pollingRef.current);
          setErrorMessage(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
        }
      })();
    }, REFRESH_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [enabled, exportedReportId, workspaceId, organisationId, isConsolidation, intl]);

  return { isCompleted, errorMessage, reset };
};
